interface Props {
  onDismiss: () => void;
  className?: string;
  /**
   * Stacking class. The default suits an in-flow dropdown raised to z-30. A
   * portaled menu with its own z-index passes the step just below it (e.g.
   * `z-[49]` under a `z-50` menu), so the menu stays clickable and everything
   * else is covered.
   */
  zClass?: string;
}

/**
 * A screen-covering layer behind an open dropdown (#121).
 *
 * Without it, tapping outside a dropdown closed the dropdown AND landed on
 * whatever was under the finger - a task row, a board card - which then
 * opened. The backdrop catches that tap, so it only closes the dropdown. It is
 * dimmed on a phone (modal-sheet feel) and clear on desktop.
 *
 * Render it as a SIBLING of the dropdown's positioned container and raise that
 * container above it (z-30 over this z-20), so the dropdown and its trigger
 * stay clickable.
 *
 * Its mousedown is stopped before it reaches `document`: the dropdowns close
 * on a document-level outside-mousedown, and closing on mousedown would
 * unmount this layer mid-tap and let the click fall through to the element
 * underneath. It dismisses on click instead - the end of the tap.
 */
export function DismissBackdrop({ onDismiss, className = '', zClass = 'z-20' }: Props) {
  return (
    <div
      data-testid="dismiss-backdrop"
      aria-hidden="true"
      className={`fixed inset-0 ${zClass} bg-black/40 sm:bg-transparent ${className}`}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onDismiss();
      }}
    />
  );
}
